import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import ModalTransicionEtapa from './ModalTransicionEtapa';
import ModalWhatsApp from './ModalWhatsApp';
import {
  resolverIdiomaPropietarioCaptacion,
  buildCaptacionWaQuickReplies,
} from '../utils/captacionIdioma';
import ModalEditarCitaCaptacion from './ModalEditarCitaCaptacion';
import ModalCrearTareaCaptacion from './ModalCrearTareaCaptacion';
import { PuntoWaHistorial } from './historial/PuntoWaHistorial';
import { IconoWhatsAppHistorial, tileWhatsAppHistorialFicha, WA_VERDE_OFICIAL } from './historial/IconoWhatsAppHistorial';
import { IconoLlamadaHistorial, tileLlamadaHistorialFicha, LLAMADA_AMARILLO } from './historial/IconoLlamadaHistorial';
import { crearCitaCaptacionAgenda, completarTareaActivaCaptacion } from '../utils/crearCitaCaptacionAgenda';
import {
  HISTORIAL_FILTRO_CHIPS,
  categoriasHistorial,
  esHistorialCitaCaptacion,
  etiquetaWhatsAppEnviadoHistorial,
  etiquetaHistorialConAutor,
  tipoVisualHistorial,
  parseHistorialMetadata,
} from '../utils/historialFiltros';
import usePermiso from '../hooks/usePermiso';
import { dialogs } from '../context/DialogContext';

function tileIconoHistorial(visual, themeColors) {
  const c = themeColors || {};
  const wa = tileWhatsAppHistorialFicha(13);
  const ll = tileLlamadaHistorialFicha();
  const mail = {
    bg: c.btnEmailBg || '#b45309',
    sh: c.btnEmailShadow || '0 2px 6px rgba(180,83,9,0.25)',
    ic: (
      <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
        <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 6L2 7" />
      </svg>
    ),
  };
  const cita = {
    bg: c.btnCitaBg || 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    sh: c.btnCitaShadow || '0 2px 6px rgba(139,92,246,0.3)',
    ic: (
      <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
  };
  const nota = {
    bg: c.btnNotaBg || 'linear-gradient(135deg, #6b7280, #4b5563)',
    sh: c.btnNotaShadow || '0 2px 6px rgba(107,114,128,0.3)',
    ic: (
      <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    ),
  };
  const sistema = {
    bg: 'linear-gradient(135deg, #475569, #64748b)',
    sh: '0 2px 6px rgba(71,85,105,0.25)',
    ic: (
      <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
        <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 102.13-9.36L1 10" />
      </svg>
    ),
  };
  const map = {
    whatsapp_enviado: wa,
    whatsapp_recibido: wa,
    email_enviado: mail,
    email_recibido: mail,
    llamada: ll,
    llamada_ana: ll,
    cita_creada: cita,
    nota,
    etapa_cambio: sistema,
  };
  return map[visual] || nota;
}

function accentHistorial(visual) {
  if (visual === 'whatsapp_enviado' || visual === 'whatsapp_recibido') return WA_VERDE_OFICIAL;
  if (visual === 'llamada' || visual === 'llamada_ana') return LLAMADA_AMARILLO;
  if (visual === 'email_enviado' || visual === 'email_recibido') return '#b45309';
  if (visual === 'cita_creada') return '#8b5cf6';
  if (visual === 'etapa_cambio') return '#475569';
  return '#6b7280';
}

function tituloFilaHistorial(item) {
  const visual = tipoVisualHistorial(item);
  if (visual === 'whatsapp_enviado') return etiquetaWhatsAppEnviadoHistorial(item);
  if (visual === 'whatsapp_recibido') return etiquetaHistorialConAutor('WhatsApp recibido', item);
  if (visual === 'email_enviado') return etiquetaHistorialConAutor('Email enviado', item);
  if (visual === 'email_recibido') return etiquetaHistorialConAutor('Email recibido', item);
  if (visual === 'llamada') return etiquetaHistorialConAutor('Llamada', item);
  if (visual === 'llamada_ana') return etiquetaHistorialConAutor('Llamada Ana (IA)', item);
  if (visual === 'cita_creada') {
    const meta = parseHistorialMetadata(item);
    let base = 'Cita programada';
    if (meta.origen === 'cita_anulada') base = 'Cita anulada';
    else if (meta.origen === 'visita_no_hecha') base = 'Visita no realizada';
    else {
      const cont = String(item?.contenido || '').toLowerCase();
      if (cont.includes('anulada')) base = 'Cita anulada';
      else if (cont.includes('no realizada')) base = 'Visita no realizada';
    }
    return etiquetaHistorialConAutor(base, item);
  }
  if (visual === 'nota') return etiquetaHistorialConAutor('Nota', item);
  if (visual === 'etapa_cambio') return etiquetaHistorialConAutor(item?.asunto || 'Cambio de etapa', item);
  return etiquetaHistorialConAutor(item?.asunto || item?.tipo || 'Actividad', item);
}

function categoriaFiltroH(item) {
  const cats = categoriasHistorial(item);
  if (cats.includes('whatsapp')) return 'whatsapp';
  if (cats.includes('mail')) return 'mail';
  if (cats.includes('llamadas')) return 'llamadas';
  if (cats.includes('citas')) return 'citas';
  if (cats.includes('notas')) return 'notas';
  if (cats.includes('sistema')) return 'sistema';
  return null;
}

const TRANSICION_ETAPA = {
  precaptacion:   { tipo: 'contacto', label: 'Registrar contacto y avanzar', icon: '💬', color: '#f97316', etapaDestino: 'contactado_cap' },
  contactado_cap: { tipo: 'cita',     label: 'Agendar visita y avanzar',     icon: '📅', color: '#ef4444', etapaDestino: 'visita_cap' },
};

/** Nombre + primer apellido del usuario CRM (plantillas WA captación). */
function nombreAgenteParaWa(usuario) {
  const raw = String(usuario?.nombre || '').trim();
  if (!raw) return 'Vitalcasa';
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]} ${parts[1]}`;
  return parts[0] || 'Vitalcasa';
}

function saludoDiaTardeMadrid() {
  try {
    const h = Number(new Intl.DateTimeFormat('es-ES', {
      timeZone: 'Europe/Madrid',
      hour: 'numeric',
      hour12: false,
    }).format(new Date()));
    return h < 14 ? 'Buenos días' : 'Buenas tardes';
  } catch {
    return new Date().getHours() < 14 ? 'Buenos días' : 'Buenas tardes';
  }
}

function calleParaWaSaludo(cap) {
  let calle = String(cap?.calle || '').trim();
  if (!calle) return '';
  calle = calle.replace(/^(calle|c\/|avda\.?|avenida|plaza|paseo|camino|partida|urbanizaci[oó]n)\s+/i, '').trim();
  return calle;
}

function textoSaludoInicialWa(captacion, usuario) {
  const saludo = saludoDiaTardeMadrid();
  const agente = nombreAgenteParaWa(usuario);
  let tipo = String(captacion?.tipo_inmueble || '').trim();
  if (!tipo || /^[0-9a-f-]{36}$/i.test(tipo)) {
    tipo = String(captacion?.datos_extras?.tipo || captacion?.datos_extras?.tipo_inmueble || '').trim() || 'inmueble';
  }
  if (/^[0-9a-f-]{36}$/i.test(tipo)) tipo = 'inmueble';
  const dir = calleParaWaSaludo(captacion);
  const trozoDir = dir
    ? ` de la calle ${dir}`
    : (captacion?.poblacion ? ` en ${String(captacion.poblacion).trim()}` : '');
  return `${saludo}, soy ${agente} de la agencia Vitalcasa, una de las más importantes de la comarca, he visto que vende su ${tipo}${trozoDir}, me interesa mucho porque tenemos clientes para esa zona. ¿En qué precio lo tiene en venta?`;
}

/**
 * layout:
 *  - 'sidebar' (por defecto): columna lateral completa (acciones + avanzar etapa + historial).
 *  - 'bottom': bloque inferior completo, con altura fija.
 *  - 'split': no pinta nada en su propio sitio del árbol; envía "Acciones rápidas"
 *    (vía accionesTarget) e "Historial" (vía historialTarget) a contenedores DOM
 *    externos usando portales, para que el modal padre los coloque en columnas
 *    distintas. En este modo nunca se muestra el botón de avanzar etapa.
 */
export default function PanelHistorialCaptacion({
  captacion, isDark, c, onAvanzado, layout = 'sidebar', ocultarTareaPendiente = false,
  accionesTarget = null, historialTarget = null,
}) {
  const { usuario } = useAuth();
  const { puede } = usePermiso();
  const verHistWhatsapp = puede('pl_capt_hist_whatsapp', 'ver');
  const verHistLlamada = puede('pl_capt_hist_llamada', 'ver');
  const verHistNota = puede('pl_capt_hist_nota', 'ver');
  const verHistRegistrarAvanzar = puede('pl_capt_hist_registrar_avanzar', 'ver');
  const verHistAgendarAvanzar = puede('pl_capt_hist_agendar_avanzar', 'ver');
  const [historial, setHistorial] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [notaTexto, setNotaTexto] = useState('');
  const [mostrarNota, setMostrarNota] = useState(false);
  const [filtro, setFiltro] = useState('todos');
  const [reload, setReload] = useState(0);
  const [transicion, setTransicion] = useState(false);
  const [tareaActiva, setTareaActiva] = useState(null);
  const [modalWA, setModalWA] = useState(false);
  const [modalCrearTarea, setModalCrearTarea] = useState(false);
  const [citaEditando, setCitaEditando] = useState(null);
  const [abriendoCita, setAbriendoCita] = useState(false);

  const esBottom = layout === 'bottom';
  const esSplit = layout === 'split';

  const cargarHistorial = () => {
    if (!captacion?.id) return;
    api.get(`/captaciones/${captacion.id}/historial`)
      .then(r => setHistorial(r.data?.data || []))
      .catch(() => {});
    api.get(`/tareas/captacion/${captacion.id}/activa`)
      .then(r => setTareaActiva(r.data?.tarea || null))
      .catch(() => setTareaActiva(null));
  };

  useEffect(() => {
    if (!captacion?.id) return;
    setCargando(true);
    api.get(`/captaciones/${captacion.id}/historial`)
      .then(r => setHistorial(r.data?.data || []))
      .catch(() => setHistorial([]))
      .finally(() => setCargando(false));
    api.get(`/tareas/captacion/${captacion.id}/activa`)
      .then(r => setTareaActiva(r.data?.tarea || null))
      .catch(() => setTareaActiva(null));
  }, [captacion?.id, reload]);

  useEffect(() => {
    if (!captacion?.id) return;
    const timer = setInterval(cargarHistorial, 3_000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captacion?.id]);

  // SSE: mensaje WA entrante de esta captación → refrescar historial al instante
  useEffect(() => {
    if (!captacion?.id) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const base = window.location.hostname === 'localhost'
      ? 'http://localhost:4000/api'
      : `${window.location.origin}/api`;
    const es = new EventSource(`${base}/whatsapp/qr-stream?token=${encodeURIComponent(token)}`);
    es.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data?.type === 'mensaje_entrante' && String(data.captacion_id) === String(captacion.id)) {
          setReload((r) => r + 1);
        }
      } catch { /* ignore */ }
    };
    es.onerror = () => { /* el navegador reintenta; no spamear */ };
    return () => es.close();
  }, [captacion?.id]);

  const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })
      + ' ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const guardarNota = async () => {
    if (!notaTexto.trim() || !captacion?.id) return;
    try {
      await api.post(`/captaciones/${captacion.id}/historial`, { tipo: 'nota', contenido: notaTexto.trim() });
      setNotaTexto('');
      setMostrarNota(false);
      setReload(r => r + 1);
    } catch { /* silent */ }
  };

  const confirmarTransicion = async (historial_data) => {
    if (!captacion?.id) return;

    const resultado = historial_data.resultado;
    const nombreProp = [captacion.nombre, captacion.apellido].filter(Boolean).join(' ') || 'propietario';
    const agenteId = captacion.agente_id || usuario?.agente_id || null;

    if (tareaActiva?.id) {
      const resTexto = resultado === 'cita' ? 'Cita agendada'
        : resultado === 'fria' ? 'Enviado a Frías'
          : 'Contacto realizado — etapa avanzada';
      await api.put(`/tareas/${tareaActiva.id}/completar`, { resultado: resTexto }).catch(() => {});
    } else if (resultado === 'cita') {
      await completarTareaActivaCaptacion(api, captacion.id);
    }

    if (resultado === 'fria') {
      await api.patch(`/captaciones/${captacion.id}/etapa`, {
        etapa: 'fria',
        historial_data: {
          tipo: 'sistema',
          contenido: `Enviado a Frías. Motivo: ${historial_data.motivo}`,
        },
      });
      if (historial_data.recontactar_fecha) {
        const desc = historial_data.rec_desc || `Recontactar con ${nombreProp}`;
        await api.post('/tareas', {
          captacion_id: captacion.id,
          agente_id: agenteId,
          tipo: 'contactar',
          descripcion: desc,
          prioridad: 'normal',
          fecha_vencimiento: historial_data.recontactar_fecha,
        }).catch(() => {});
      }
      setTransicion(false);
      onAvanzado?.();
      return;
    }

    if (resultado === 'cita' && historial_data.fecha_accion) {
      await api.patch(`/captaciones/${captacion.id}/etapa`, {
        etapa: 'visita_cap',
        historial_data,
      });
      try {
        await crearCitaCaptacionAgenda(api, { captacion, historial_data, agenteId });
      } catch (e) {
        console.error('[Precaptación] Error creando cita en agenda:', e);
      }
      setTransicion(false);
      onAvanzado?.();
      return;
    }

    const tr = TRANSICION_ETAPA[captacion.etapa];
    if (tr) {
      await api.patch(`/captaciones/${captacion.id}/etapa`, {
        etapa: tr.etapaDestino,
        historial_data,
      });
    }

    setTransicion(false);
    onAvanzado?.();
  };

  const parseTareaMeta = (tarea) => {
    const m = tarea?.metadata;
    if (!m) return {};
    if (typeof m === 'object') return m;
    try { return JSON.parse(m || '{}'); } catch { return {}; }
  };

  const resolverAgendaIdDesdeHistorial = async (item) => {
    const meta = parseHistorialMetadata(item);
    if (meta.agenda_id) return String(meta.agenda_id);

    const fromTask = parseTareaMeta(tareaActiva).agenda_id;
    if (fromTask && (item?.tipo || '').toLowerCase() === 'cita') return String(fromTask);

    if (!captacion?.id) return null;
    try {
      const res = await api.get(`/agenda/captacion/${captacion.id}`);
      const citas = res.data?.data || [];
      if (!citas.length) return null;

      const fa = item?.fecha_accion ? new Date(item.fecha_accion).getTime() : null;
      if (fa && Number.isFinite(fa)) {
        const match = citas.find((c) => {
          const ti = c.fecha_inicio ? new Date(c.fecha_inicio).getTime() : NaN;
          return Number.isFinite(ti) && Math.abs(ti - fa) < 120000;
        });
        if (match?.id) return String(match.id);
      }

      const activa = citas.find((c) => !['cancelada', 'cancelado'].includes(String(c.estado || '').toLowerCase()));
      if (activa?.id) return String(activa.id);
      return citas[0]?.id ? String(citas[0].id) : null;
    } catch {
      return null;
    }
  };

  const abrirEdicionCita = async (item) => {
    if (!esHistorialCitaCaptacion(item) || abriendoCita) return;
    setAbriendoCita(true);
    try {
      const agendaId = await resolverAgendaIdDesdeHistorial(item);
      if (!agendaId) {
        await dialogs.alert('No se encontró la cita en agenda para editarla.');
        return;
      }
      const res = await api.get(`/agenda/${agendaId}`);
      const cita = res.data?.data || res.data;
      if (!cita?.id) {
        await dialogs.alert('No se pudo cargar la cita.');
        return;
      }
      setCitaEditando(cita);
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || e?.message || 'No se pudo abrir la cita.');
    } finally {
      setAbriendoCita(false);
    }
  };

  const guardarEdicionCita = async ({ fecha_inicio, fecha_fin }) => {
    if (!citaEditando?.id) return;
    await api.put(`/agenda/${citaEditando.id}`, {
      id: citaEditando.id,
      fecha_inicio,
      fecha_fin,
      titulo: citaEditando.titulo,
      descripcion: citaEditando.descripcion,
      tipo: citaEditando.tipo || 'captacion',
      estado: citaEditando.estado || 'pendiente',
      agente_id: citaEditando.agente_id || null,
      usuario_id: citaEditando.usuario_id || null,
      captacion_id: citaEditando.captacion_id || captacion?.id || null,
      direccion: citaEditando.direccion || null,
      notas: citaEditando.notas || null,
    });
    setCitaEditando(null);
    setReload((r) => r + 1);
    onAvanzado?.();
  };

  const anularCitaDesdeModal = async () => {
    if (!citaEditando?.id) return;
    if (!(await dialogs.confirm(
      '¿Anular esta cita?\n\nLa captación volverá a Contactados y se cerrará la tarea de visita.'
    ))) return;
    try {
      const { data } = await api.post(`/agenda/${citaEditando.id}/cancelar`);
      if (data?.ok === false) {
        await dialogs.alert(data?.error || 'No se pudo anular la cita.');
        return;
      }
      setCitaEditando(null);
      setReload((r) => r + 1);
      onAvanzado?.();
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || e?.message || 'No se pudo anular la cita.');
    }
  };

  const transicionInfo = TRANSICION_ETAPA[captacion?.etapa];
  const verAvanzarEtapa = transicionInfo?.tipo === 'cita' ? verHistAgendarAvanzar : verHistRegistrarAvanzar;
  const mostrarAvanzarEtapa = !!transicionInfo && !esSplit && verAvanzarEtapa;
  const filtrado = useMemo(() => {
    if (filtro === 'todos') return historial;
    return historial.filter((i) => categoriaFiltroH(i) === filtro);
  }, [historial, filtro]);

  const conteosFiltro = useMemo(() => {
    const counts = {};
    for (const f of HISTORIAL_FILTRO_CHIPS) {
      if (f.id === 'todos') continue;
      counts[f.id] = historial.filter((i) => categoriaFiltroH(i) === f.id).length;
    }
    return counts;
  }, [historial]);

  const containerStyle = esBottom
    ? {
        flexShrink: 0,
        height: 260,
        borderTop: `1px solid ${c.border}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: c.bgPanel || c.bgCard,
      }
    : {
        flex: 1,
        minWidth: 0,
        borderLeft: `1px solid ${c.border}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      };

  const listaStyle = (esBottom || esSplit)
    ? { flex: 1, overflowY: 'auto', padding: '6px 20px', minHeight: 0 }
    : { flex: 1, overflowY: 'auto', padding: '8px 14px' };

  const ocultarTarea = ocultarTareaPendiente || captacion?.etapa === 'precaptacion';

  const tareaActivaJSX = !ocultarTarea && tareaActiva && (() => {
    const vence = tareaActiva.fecha_vencimiento ? new Date(tareaActiva.fecha_vencimiento) : null;
    const ahora = new Date();
    const diffMs = vence ? vence - ahora : null;
    const diffH = diffMs !== null ? Math.floor(diffMs / 3600000) : null;
    const vencida = diffMs !== null && diffMs < 0;
    const urgente = diffMs !== null && !vencida && diffH < 4;
    const colorT = vencida ? '#ef4444' : urgente ? '#f97316' : '#eab308';
    const bgT = vencida ? (isDark ? 'rgba(239,68,68,0.12)' : '#fef2f2')
      : urgente ? (isDark ? 'rgba(249,115,22,0.12)' : '#fff7ed')
        : (isDark ? 'rgba(234,179,8,0.12)' : '#fefce8');
    const tiempoLabel = vencida
      ? `Vencida hace ${Math.abs(diffH)}h`
      : diffH !== null ? (diffH < 1 ? '< 1h' : `${diffH}h`) : '';
    return (
      <div style={{
        margin: esSplit ? '0 0 10px' : esBottom ? '8px 20px 0' : '10px 14px 0',
        padding: '8px 12px',
        borderRadius: 9,
        background: bgT,
        border: `1px solid ${colorT}40`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: colorT, letterSpacing: '0.06em' }}>
            📋 Tarea pendiente
          </span>
          {tiempoLabel && (
            <span style={{ fontSize: 10, fontWeight: 700, color: colorT, background: `${colorT}20`, padding: '1px 7px', borderRadius: 8 }}>
              {vencida ? '⚠️ ' : '⏱ '}{tiempoLabel}
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: c.textPrimary }}>{tareaActiva.descripcion}</div>
        {tareaActiva.agente_nombre && (
          <div style={{ fontSize: 10, color: c.textMuted, marginTop: 2 }}>👤 {tareaActiva.agente_nombre}</div>
        )}
      </div>
    );
  })();

  const accionesRapidasJSX = (() => {
    const btnAccion = {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 3,
      padding: '6px 0',
      borderRadius: 6,
      cursor: 'pointer',
      border: `1px solid ${c.btnActionBorder || c.border}`,
      background: c.btnActionBg || c.bgInput,
      fontSize: 10,
      fontWeight: 500,
      color: c.btnActionText || c.textSecondary,
      fontFamily: 'inherit',
    };
    const tile = (bg, sh, ic) => (
      <div style={{
        width: 30, height: 30, borderRadius: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: bg, boxShadow: sh,
      }}>
        {ic}
      </div>
    );
    const icWa = <IconoWhatsAppHistorial size={15} />;
    const icCall = <IconoLlamadaHistorial size={22} />;
    const icNota = (
      <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    );
    const icTarea = (
      <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11l3 3L22 4" />
        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
      </svg>
    );

    return (
    <div style={esSplit ? {
      backgroundColor: c.bgPanel, borderRadius: 10, padding: 14,
    } : {
      padding: esBottom ? '10px 20px' : '12px 14px',
      borderBottom: `1px solid ${c.border}`,
      flexShrink: 0,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: c.textMuted, marginBottom: 8 }}>
        Acciones rápidas
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        {verHistWhatsapp && captacion?.telefono && (
          <button type="button" onClick={() => setModalWA(true)} style={btnAccion}>
            {tile(c.btnWhatsappBg || 'linear-gradient(135deg, #25d366, #128c7e)', c.btnWhatsappShadow || '0 2px 6px rgba(37,211,102,0.3)', icWa)}
            WhatsApp
          </button>
        )}
        {verHistLlamada && captacion?.telefono && (
          <button type="button" onClick={() => window.open(`tel:${captacion.telefono}`, '_blank')} style={btnAccion}>
            {tile(c.btnCallBg || 'linear-gradient(135deg, #fde047, #eab308)', c.btnCallShadow || '0 2px 6px rgba(234,179,8,0.35)', icCall)}
            Llamada
          </button>
        )}
        {verHistNota && (
          <button
            type="button"
            onClick={() => setMostrarNota((v) => !v)}
            style={{
              ...btnAccion,
              border: `1px solid ${mostrarNota ? '#a855f7' : (c.btnActionBorder || c.border)}`,
              background: mostrarNota ? 'rgba(168,85,247,0.12)' : (c.btnActionBg || c.bgInput),
              color: mostrarNota ? '#a855f7' : (c.btnActionText || c.textSecondary),
            }}
          >
            {tile(c.btnNotaBg || 'linear-gradient(135deg, #6b7280, #4b5563)', c.btnNotaShadow || '0 2px 6px rgba(107,114,128,0.3)', icNota)}
            Nota
          </button>
        )}
        {captacion?.id && (
          <button type="button" onClick={() => setModalCrearTarea(true)} style={btnAccion}>
            {tile('linear-gradient(135deg, #3b82f6, #2563eb)', '0 2px 6px rgba(59,130,246,0.35)', icTarea)}
            Crear Tarea
          </button>
        )}
      </div>
      {verHistNota && mostrarNota && (
        <div style={{ marginTop: 8 }}>
          <textarea value={notaTexto} onChange={e => setNotaTexto(e.target.value)}
            placeholder="Escribe una nota..." rows={esBottom || esSplit ? 2 : 3}
            style={{ width: '100%', padding: '7px 10px', fontSize: 12, borderRadius: 8, border: `1px solid ${c.border}`, background: c.bgInput, color: c.textPrimary, fontFamily: 'inherit', resize: 'none', outline: 'none', boxSizing: 'border-box' }} />
          <button onClick={guardarNota} style={{ marginTop: 4, width: '100%', padding: '6px', borderRadius: 7, border: 'none', background: '#a855f7', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            Guardar nota
          </button>
        </div>
      )}
    </div>
    );
  })();

  const avanzarEtapaJSX = mostrarAvanzarEtapa && (
    <div style={{
      padding: esBottom ? '0 20px 10px' : '10px 14px',
      borderBottom: `1px solid ${c.border}`,
      flexShrink: 0,
    }}>
      <button
        onClick={() => setTransicion(true)}
        style={{
          width: '100%', padding: '10px 12px', borderRadius: 9, border: 'none',
          background: `linear-gradient(135deg, ${transicionInfo.color}cc, ${transicionInfo.color})`,
          color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
          fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 8, boxShadow: `0 4px 12px ${transicionInfo.color}40`,
        }}
      >
        <span style={{ fontSize: 16 }}>{transicionInfo.icon}</span>
        {transicionInfo.label}
        <span style={{ marginLeft: 2 }}>→</span>
      </button>
    </div>
  );

  const historialHeaderJSX = (
    <div style={{ padding: (esBottom || esSplit) ? '8px 20px 6px' : '10px 14px 8px', borderBottom: `1px solid ${c.border}`, flexShrink: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: c.textMuted, marginBottom: 6 }}>Historial</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {HISTORIAL_FILTRO_CHIPS.map(f => (
          <button key={f.id} onClick={() => setFiltro(f.id)} style={{
            padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
            border: `1.5px solid ${filtro === f.id ? '#3b82f6' : c.border}`,
            background: filtro === f.id ? '#3b82f6' : c.bgInput,
            color: filtro === f.id ? '#fff' : c.textMuted,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>
            {f.label}
            {f.id !== 'todos' && <span style={{ opacity: 0.75, fontSize: 9 }}> ({conteosFiltro[f.id] || 0})</span>}
          </button>
        ))}
      </div>
    </div>
  );

  const historialListaJSX = (
    <div style={listaStyle}>
      {cargando && <div style={{ fontSize: 11, color: c.textMuted, textAlign: 'center', marginTop: 12 }}>Cargando…</div>}
      {!cargando && filtrado.length === 0 && (
        <div style={{ fontSize: 11, color: c.textMuted, textAlign: 'center', marginTop: 12, lineHeight: 1.8 }}>Sin actividad registrada</div>
      )}
      {filtrado.map((item, i) => {
        const esCitaRow = esHistorialCitaCaptacion(item);
        const visual = tipoVisualHistorial(item);
        const tile = tileIconoHistorial(visual, c);
        const accent = accentHistorial(visual);
        const titulo = tituloFilaHistorial(item);
        const detalle = (() => {
          if (visual === 'etapa_cambio') {
            const cont = String(item.contenido || '').trim();
            const meta = parseHistorialMetadata(item);
            const ex = (() => {
              try {
                return typeof captacion?.datos_extras === 'object' && captacion?.datos_extras
                  ? captacion.datos_extras
                  : JSON.parse(captacion?.datos_extras || '{}');
              } catch { return {}; }
            })();
            const motivo = String(
              meta.motivo
              || ex.motivo_eliminacion
              || ''
            ).trim();
            const por = String(
              meta.eliminado_por
              || ex.eliminado_por
              || item.actor_nombre
              || item.actor
              || ''
            ).trim();
            const esElim =
              String(meta.etapa_destino || '').toLowerCase() === 'eliminado'
              || /archivada en eliminad/i.test(cont)
              || meta.origen === 'marketplace_no_captar';
            if (esElim) {
              if (/motivo:\s*.+\s*—\s*por:/i.test(cont)) return cont;
              const m = motivo
                || (cont && !/^archivada en eliminad/i.test(cont) && !/^eliminada desde/i.test(cont) ? cont : '')
                || 'Sin motivo indicado';
              const quien = por || 'Usuario';
              return `Archivada en Eliminados — Motivo: ${m} — Por: ${quien}`;
            }
            if (String(meta.etapa_destino || '').toLowerCase() === 'fria' || /enviada a fr[ií]os/i.test(cont)) {
              if (/motivo:\s*.+\s*—\s*por:/i.test(cont) || /—\s*por:/i.test(cont)) return cont || null;
              if (motivo) return `Enviada a Fríos — Motivo: ${motivo} — Por: ${por || 'Usuario'}`;
            }
            return cont || null;
          }
          if (!item.contenido) return null;
          if (visual === 'cita_creada') {
            const cont = String(item.contenido || '').trim();
            // Evitar repetir el título genérico
            if (!cont || cont.toLowerCase() === 'cita') return null;
            return cont;
          }
          return item.contenido;
        })();

        return (
          <div
            key={item.id || i}
            role={esCitaRow ? 'button' : undefined}
            tabIndex={esCitaRow ? 0 : undefined}
            onClick={esCitaRow ? () => abrirEdicionCita(item) : undefined}
            onKeyDown={esCitaRow ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                abrirEdicionCita(item);
              }
            } : undefined}
            title={esCitaRow ? 'Abrir / editar cita' : undefined}
            style={{
              display: 'flex', gap: 8, padding: '8px 0',
              borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
              alignItems: 'flex-start',
              cursor: esCitaRow ? 'pointer' : 'default',
              borderLeft: isDark ? undefined : `3px solid ${accent}`,
              paddingLeft: isDark ? 0 : 6,
            }}
          >
            <div style={{
              width: 28, height: 28, borderRadius: 7, display: 'flex',
              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              background: tile.bg, boxShadow: tile.sh,
            }}>
              {tile.ic}
            </div>
            <div style={{ flexShrink: 0, width: 44, textAlign: 'center', paddingTop: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: c.textSecondary, lineHeight: 1.2 }}>
                {item.created_at ? new Date(item.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }) : '—'}
              </div>
              <div style={{ fontSize: 9, color: c.textMuted, lineHeight: 1.2 }}>
                {item.created_at ? new Date(item.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : ''}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: c.textPrimary, display: 'flex', alignItems: 'center', gap: 6, lineHeight: 1.3 }}>
                <PuntoWaHistorial tipo={visual} />
                <span>{titulo}</span>
                {esCitaRow && (
                  <span style={{ marginLeft: 2, fontSize: 9, color: '#3b82f6', fontWeight: 600 }}>Editar</span>
                )}
              </div>
              {detalle && (
                <div style={{
                  fontSize: 11, color: c.textSecondary, lineHeight: 1.4, marginTop: 2,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {detalle}
                </div>
              )}
              {item.fecha_accion && (
                <div style={{ fontSize: 9, color: c.textMuted, marginTop: 2 }}>📅 {fmt(item.fecha_accion)}</div>
              )}
              <div style={{ fontSize: 9, color: c.textMuted, marginTop: 1 }}>{item.actor || 'Sistema'}</div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const modalesJSX = (
    <>
      <ModalTransicionEtapa
        isOpen={transicion}
        tipo={transicionInfo?.tipo}
        captacion={captacion}
        onClose={() => setTransicion(false)}
        onConfirmar={confirmarTransicion}
      />

      <ModalEditarCitaCaptacion
        isOpen={!!citaEditando}
        cita={citaEditando}
        captacion={captacion}
        onClose={() => setCitaEditando(null)}
        onGuardar={guardarEdicionCita}
        onCancelarCita={anularCitaDesdeModal}
      />

      <ModalCrearTareaCaptacion
        isOpen={modalCrearTarea}
        captacion={captacion}
        onClose={() => setModalCrearTarea(false)}
        onCreada={() => setReload((r) => r + 1)}
      />

      {modalWA && (() => {
        const idiomaProp = resolverIdiomaPropietarioCaptacion(captacion);
        return (
        <ModalWhatsApp
          isOpen={modalWA}
          onClose={() => setModalWA(false)}
          agenteTarjetaUrl={captacion?.agente_tarjeta_url || null}
          crmContext={{ captacion_id: captacion?.id }}
          idioma={idiomaProp}
          contact={{
            nombre: [captacion?.nombre, captacion?.apellido].filter(Boolean).join(' ') || 'Propietario',
            telefono: captacion?.telefono,
            iniciales: [captacion?.nombre?.[0], captacion?.apellido?.[0]].filter(Boolean).join('').toUpperCase() || '?',
          }}
          quickReplies={[
            ...(String(idiomaProp).toLowerCase() === 'es'
              ? [{ id: 'presentacion', emoji: '👋', label: 'Saludo', text: textoSaludoInicialWa(captacion, usuario) }]
              : []),
            ...buildCaptacionWaQuickReplies(idiomaProp),
          ]}
          onSend={async ({ mensaje }) => {
            if (!captacion?.id) return;
            const texto = mensaje || 'WhatsApp enviado';
            const ahora = new Date().toISOString();
            // UI inmediata (la persistencia la hace POST /whatsapp/enviar con captacion_id)
            setHistorial((prev) => [{
              id: `tmp-wa-${ahora}`,
              tipo: 'whatsapp_enviado',
              canal: 'whatsapp',
              contenido: texto,
              created_at: ahora,
              actor: 'Tú',
            }, ...prev]);
            setReload((r) => r + 1);
            if (captacion.etapa === 'precaptacion') {
              try {
                if (tareaActiva?.id) {
                  await api.put(`/tareas/${tareaActiva.id}/completar`, { resultado: 'Contacto realizado vía WhatsApp' });
                }
                await api.patch(`/captaciones/${captacion.id}/etapa`, {
                  etapa: 'contactado_cap',
                  historial_data: {
                    tipo: 'sistema',
                    contenido: 'Avanzado automáticamente a Contactados tras envío de WhatsApp.',
                  },
                });
                setReload(r => r + 1);
                onAvanzado?.();
              } catch (err) {
                console.error('[PanelHistorial] Error auto-avanzando etapa:', err?.response?.data || err.message);
                setReload(r => r + 1);
              }
            }
          }}
        />
        );
      })()}
    </>
  );

  if (esSplit) {
    return (
      <>
        {accionesTarget && createPortal((
          <>
            {tareaActivaJSX}
            {accionesRapidasJSX}
          </>
        ), accionesTarget)}
        {historialTarget && createPortal((
          <>
            {historialHeaderJSX}
            {historialListaJSX}
          </>
        ), historialTarget)}
        {modalesJSX}
      </>
    );
  }

  return (
    <div style={containerStyle}>
      {!esBottom && <div style={{ height: 3, background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)' }} />}
      {tareaActivaJSX}
      {accionesRapidasJSX}
      {avanzarEtapaJSX}
      {historialHeaderJSX}
      {historialListaJSX}
      {modalesJSX}
    </div>
  );
}
